import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { IntakeFormStatus } from 'api/crm/intakeFormAPI';

// components
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import { IconSVG, Text } from '@ui';
import { ReactComponent as DraftIcon } from 'resources/icons/remix-icons/edit-line.svg';
import { ReactComponent as ReturnedIcon } from 'resources/icons/remix-icons/arrow-left-circle-fill.svg';
import { ReactComponent as SubmittedIcon } from 'resources/icons/remix-icons/checkbox-circle-fill.svg';

interface IProps {
  status: IntakeFormStatus;
}

const ApplicationStatus = ({ status }: IProps) => {
  const { t } = useTranslation();

  const statusBarProps = useMemo(() => {
    switch (status) {
      case 'returned':
        return {
          icon: ReturnedIcon,
          color: colorsTheme.colorWarning,
          text: t('intake_form.returned.title'),
        };

      case 'submitted':
        return {
          icon: SubmittedIcon,
          color: colorsTheme.colorSuccessD1,
          text: t('intake_form.submitted.title'),
        };

      case 'draft':
        return {
          icon: DraftIcon,
          color: colorsTheme.colorDarkL4,
          text: t('intake_form.draft.title'),
        };

      default:
        return null;
    }
  }, [status]);

  if (!statusBarProps) {
    return null;
  }

  return (
    <StatusBar $color={statusBarProps.color}>
      <DivAlignCenter>
        <StyledIconSVG
          component={statusBarProps.icon}
          size="small"
          color={colorsTheme.colorWhite}
        />
        <Text variant="h5" color={colorsTheme.colorWhite}>
          {statusBarProps.text}
        </Text>
      </DivAlignCenter>
    </StatusBar>
  );
};

const StatusBar = styled.div<{ $color: string }>`
  width: 100%;
  padding: ${({ theme }) => `${theme.paddingXl} ${theme.paddingXl}`};
  margin-bottom: ${({ theme }) => theme.marginSm};
  background-color: ${({ $color }) => $color};
  color: ${colorsTheme.colorWhite};
`;

const StyledIconSVG = styled(IconSVG)`
  margin-right: ${({ theme }) => theme.marginXs};
`;

export default ApplicationStatus;
